import React, { useEffect, useMemo, useState } from "react";
import PropertySelect from "./PropertySelect";
import RecipientItem from "../CampaignSteps/components/RecipientItem";
import { PropertyState, RoleOption } from "./Step3.types";
import { getOptionsByProperties } from "PhishingManager/service/index.Service";
import DebounceInput from "PhishingManager/components/DebouncedInput";
import SelectedLabel from "./SelectedLabel";
import { useTranslation } from "react-i18next";

interface Props {
    loading: boolean;
    tenentId: number;
    propertyData: PropertyState;
    roleOptions: RoleOption[];
    selectedRoles: RoleOption[];
    selectedProperties: number[];
    allPropertiesSelected: boolean;
    setRoleOptions: (roles: RoleOption[]) => void;
    setSelectedRoles: (roles: RoleOption[]) => void;
    toggleLoading: (status: boolean) => void;
    setSelectedProperties: (propertyIds: number[]) => void;
    setPropertySearchQuery: (val: string | null) => void;
    moveCurrentPage: () => void;
    setAllPropertiesSelected: (val: boolean) => void;
    setSelectedUserCount: (value: number) => void;
}

const RolesRecipients: React.FC<Props> = ({
    tenentId,
    loading,
    propertyData,
    roleOptions,
    selectedRoles,
    selectedProperties,
    allPropertiesSelected,
    setRoleOptions,
    setSelectedRoles,
    toggleLoading,
    setSelectedProperties,
    setPropertySearchQuery,
    moveCurrentPage,
    setAllPropertiesSelected,
    setSelectedUserCount
}) => {
    const [search, setSearch] = useState("");
    const { t } = useTranslation();

    const filteredRoles = useMemo(() => {
        return roleOptions.filter((role) => (!search || role.label.toLowerCase().includes(search.toLowerCase())));
    }, [roleOptions, search]);

    const totalUsersCount = useMemo(() => roleOptions.reduce((acc, group) => {
        const isSelected = selectedRoles.find((s) => s.value === group.value);
        return isSelected ? acc + group.count : acc;
    }, 0), [roleOptions, selectedRoles])

    const allGroupsSelected = useMemo(() => {
        return filteredRoles.length ? filteredRoles.length === selectedRoles.length : false;
    }, [filteredRoles, selectedRoles])

    const handleSelectAllGroups = () => {
        let data: RoleOption[] = [];
        if (!allGroupsSelected) data = filteredRoles;
        setSelectedRoles(data);
    }

    const handleGroupToggle = (groupItem: RoleOption) => {
        const isSelected = selectedRoles.some((g) => g.value === groupItem.value);
        setSelectedRoles(isSelected ? selectedRoles.filter((g) => g.value !== groupItem.value) : [...selectedRoles, groupItem]);
    };

    const getData = async () => {
        try {
            if (!tenentId) return;
            toggleLoading(true);
            const res = await getOptionsByProperties("role", Number(tenentId), selectedProperties, allPropertiesSelected);
            const grouped: RoleOption[] = Object.values(
                (res?.data || []).filter((item: any) => Boolean(item.user_count)).reduce((acc: Record<number, RoleOption>, group: any) => {
                    const key = group.id;

                    if (!acc[key]) {
                        acc[key] = {
                            value: group.id,
                            label: group.description,
                            description: t(`phishingManager.campaignSetup.step3.role.description.${group.description}`) || "",
                            count: 0,
                            propertyId: []
                        };
                    }

                    acc[key].count += group?.user_count || 0;
                    acc[key].propertyId.push(group?.property_id || 0);
                    return acc;
                }, {})
            );
            setRoleOptions(grouped);
        } catch (error) {
            setRoleOptions([]);
        } finally {
            toggleLoading(false);
        }
    };

    useEffect(() => {
        if (selectedProperties.length) getData();
        else setRoleOptions([]);
    }, [selectedProperties])

    useEffect((() => {
        if (!allPropertiesSelected) setSelectedRoles([]);
    }), [allPropertiesSelected]);

    useEffect(() => {
        if (search && filteredRoles.length < selectedRoles.length) setSelectedRoles(filteredRoles);
    }, [search, filteredRoles])

    useEffect(() => {
        setSelectedUserCount(totalUsersCount);
    }, [totalUsersCount])

    return (
        <div className="phishing-step3__role-section">
            <div className="phishing-step3__role-filters">
                <div className="phishing-step3__filter-group">
                    <div className="phishing-step3__label">
                        {t('phishingManager.campaignSetup.step3.role.selectProperty')} <span className="required">*</span>
                    </div>
                    <PropertySelect {...{ t, loading, propertyData, selectedProperties, setSelectedProperties, allSelected: allPropertiesSelected, setAllSelected: setAllPropertiesSelected, moveCurrentPage, setSearchValue: setPropertySearchQuery }}
                        onPropertyDeselect={(propertyValue) => {
                            setSelectedRoles(
                                selectedRoles.filter((group) => {
                                    const selGroup = filteredRoles.find((g) => g.value === group.value);
                                    return selGroup?.propertyId.includes(propertyValue);
                                })
                            );
                        }}
                    />
                </div>

                <div className="phishing-step3__filter-group phishing-step3__role-search">
                    <div>
                        <DebounceInput placeholder={t('phishingManager.campaignSetup.step3.role.searchPlaceholder')} setDebouncedSearch={setSearch} />
                    </div>
                </div>
            </div>

            <SelectedLabel t={t} labelText={`${t("phishingManager.campaignSetup.step3.role.selectRole")} ${filteredRoles.length > 1 ? t(
                "phishingManager.campaignSetup.step3.role.results", { count: filteredRoles.length }
            ) : ""}`} totalCount={totalUsersCount} selectedAll={allGroupsSelected} handleSelectAll={handleSelectAllGroups} />

            <div className="phishing-step3__role-list">
                {selectedProperties.length === 0 && !allPropertiesSelected ? (
                    <div className="phishing-step3__role-item phishing-step3__role-item--empty">
                        {t('phishingManager.campaignSetup.step3.role.selectPropertyFirst')}
                    </div>
                ) : filteredRoles.length === 0 ? (
                    <div className="phishing-step3__role-item phishing-step3__role-item--empty">
                        {t('phishingManager.campaignSetup.step3.role.noRolesFound')}
                    </div>
                ) : (
                    <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                        {filteredRoles.map((group) => (
                            <RecipientItem
                                key={group.value}
                                title={group.label}
                                description={`${group.description} - ${t(`phishingManager.campaignSetup.step3.countUser${group.count > 1 ? "s" : ""}`, {
                                    count: group.count
                                })}`}
                                iconType={"recipientRole"}
                                isChecked={selectedRoles.some(g => g.value === group.value)}
                                onToggle={() => handleGroupToggle(group)}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default RolesRecipients;